import { Decimal } from 'decimal.js';
import { getExclusiveTax, getInclusiveTax, setTaxRound, calculateOrderLevelDistributedTax } from "@/helpers/calc";

export default {
  methods: {
    calculateOrderTax: function () {
      const ROUNDING_TYPE_NEAREST = 'nearest';
      const ROUNDING_TYPE_NO = 'no';

      const tempStore = this.$store.getters["config/store"] || {};
      const taxRoundingMode = tempStore.tax_round || ROUNDING_TYPE_NEAREST;
      const isNoRounding = taxRoundingMode === ROUNDING_TYPE_NO;

      let overallTax = new Decimal(0);

      if (isNoRounding) {
        for (const item of this.currentOrder.items) {
          if (item.isPromotionItem && item.price === 0) continue;

          if (this.products && this.products.length > 0) {
            const product = this.products.find(prod => prod.id === item.product);
            const isTaxed = product && product.tax_rates && product["tax_rates"].length > 0
                && item.tax_status === 0;

            if (!item.void_status && isTaxed && item._raw_tax !== undefined) {
              overallTax = overallTax.plus(new Decimal(item._raw_tax));
            }
          }
        }
        // For no rounding mode, we return the exact sum of raw taxes without any rounding,
        // as the final tax is calculated from these raw values.
        // This ensures that the total tax is consistent with the sum of item taxes,
        // even if individual item taxes are not rounded.
        return overallTax.toNumber();
      }

      if (this._lastDistributionResult && this._lastDistributionResult.total_tax) {
        const hasRefunds = this.currentOrder.items.some(item => item.quantity < 0);
        if (hasRefunds) {
          return setTaxRound(new Decimal(this._lastDistributionResult.total_tax).toNumber());
        }
      }

      for (const item of this.currentOrder.items) {
        if (item.isPromotionItem && item.price === 0) continue;

        if (this.products && this.products.length > 0) {
          const product = this.products.find(prod => prod.id === item.product);
          const isTaxed = product && product.tax_rates && product["tax_rates"].length > 0
              && item.tax_status === 0;

          if (!item.void_status && isTaxed) {
            if (item.tax_items && item.tax_items.length > 0) {
              for (const taxItem of item.tax_items) {
                overallTax = overallTax.plus(new Decimal(taxItem.tax_total || 0));
              }
            } else {
              overallTax = overallTax.plus(new Decimal(item.tax || 0));
            }
          }
        }
      }

      return setTaxRound(overallTax.toNumber());
    },

    getItemAllTaxSum: function (item) {
      if (!item.tax_items) return 0;

      let sum = new Decimal(0);
      for (const tax_item of item.tax_items) {
        sum = sum.plus(new Decimal(tax_item.tax_value_item || 0));
      }

      return setTaxRound(sum.toNumber());
    },

    calculateItemTaxItems: function (item) {
      if (!item) throw Error("[calculateItemTax] No item provided");
      if (!this.products || !Array.isArray(this.products)) return [];

      const product = this.products.find(prod => prod.id === item.product);
      if (!product) throw Error("[calculateItemTax] Product is missing");

      if (!product["tax_rates"] || !Array.isArray(product["tax_rates"])) return [];

      const tax_items = [];
      const self = this;

      if (!item["modifiers"]) item["modifiers"] = [];

      const quantitySign = Math.sign(item.quantity) || 1;
      const absQuantity = new Decimal(Math.abs(item.quantity));

      const tempStore = this.$store.getters["config/store"] || {};
      const taxRoundingMode = tempStore.tax_round || 'nearest';
      const isNoRounding = taxRoundingMode === 'no';

      let itemRawTaxTotal = new Decimal(0);

      for (const tax_rate of product["tax_rates"]) {
        let modTaxSum = new Decimal(0);
        const rateDecimal = new Decimal(tax_rate.rate).dividedBy(100);

        const itemDiscount = Math.abs(item.discount || 0);
        const hasWholeDiscount = (typeof item.wholeDiscount === 'number' && item.wholeDiscount !== 0);
        const hasOrderDiscountRatio = (typeof item.orderDiscountRatio === 'number' && item.orderDiscountRatio !== 0);

        const itemBaseTemp = item.price_base || item.price || 0;
        let modsSumRaw = 0;

        if (item["modifiers"] && Array.isArray(item["modifiers"])) {
          const validModifiers = item["modifiers"].filter(m => m && m.void_status !== true);
          modsSumRaw = validModifiers.reduce((sum, m) => {
            const modifierBasePrice = m.price_original != null ? m.price_original : (m.price || 0);
            return sum + modifierBasePrice;
          }, 0);
          const totalBase = itemBaseTemp + modsSumRaw;

          const isItemLevelDiscountWithModifiers = itemDiscount > itemBaseTemp && modsSumRaw > 0 && !hasWholeDiscount && !hasOrderDiscountRatio;

          for (const mod of validModifiers) {
            const modifierBasePrice = mod.price_original != null ? mod.price_original : (mod.price || 0);
            let modifierDiscountedPrice = new Decimal(modifierBasePrice);

            if (isItemLevelDiscountWithModifiers && totalBase > 0) {
              const discountRatio = new Decimal(itemDiscount).dividedBy(totalBase);
              const safeRatio = discountRatio.greaterThan(1) ? new Decimal(1) : discountRatio;
              modifierDiscountedPrice = new Decimal(modifierBasePrice).times(new Decimal(1).minus(safeRatio));
            } else if (hasOrderDiscountRatio) {
              const orderDiscountRatio = new Decimal(item.orderDiscountRatio || 0);
              const safeRatio = orderDiscountRatio.greaterThan(1) ? new Decimal(1) : orderDiscountRatio;
              modifierDiscountedPrice = new Decimal(modifierBasePrice).times(new Decimal(1).minus(safeRatio));
            }

            if (this.isTaxInclusive) {
              modTaxSum = modTaxSum.plus(new Decimal(getInclusiveTax(modifierDiscountedPrice.toNumber(), rateDecimal.toNumber())));
            } else {
              modTaxSum = modTaxSum.plus(new Decimal(getExclusiveTax(modifierDiscountedPrice.toNumber(), rateDecimal.toNumber())));
            }
          }
        }

        let productDiscount = new Decimal(item.discount || 0);
        const itemBase = new Decimal(item.price_base || item.price || 0);

        if (productDiscount.abs().greaterThan(itemBase) && item["modifiers"] && item["modifiers"].length > 0) {
          productDiscount = itemBase; // Cap at item price
        }

        const itemBaseMinusProdDiscount = itemBase.minus(productDiscount);
        let discountedPrice;

        if (hasOrderDiscountRatio) {
          const orderDiscountRatio = new Decimal(item.orderDiscountRatio || 0);
          const safeRatio = orderDiscountRatio.greaterThan(1) ? new Decimal(1) : orderDiscountRatio;
          discountedPrice = itemBaseMinusProdDiscount.times(new Decimal(1).minus(safeRatio));
        } else {
          const wholeDiscountAmount = item.wholeDiscount < 0
              ? new Decimal(item.wholeDiscount).negated()
              : new Decimal(item.wholeDiscount || 0);
          discountedPrice = itemBaseMinusProdDiscount.minus(wholeDiscountAmount);
        }

        const totalPrice = discountedPrice.times(absQuantity);

        let totalTax = new Decimal(0);
        let rawTotalTax = new Decimal(0);

        // Inclusive/Exclusive Logic:
        if (this.isTaxInclusive) {
          if (isNoRounding) {
            totalTax = new Decimal(getInclusiveTax(totalPrice.toNumber(), rateDecimal.toNumber()));
            rawTotalTax = totalTax;
          } else {
            const singleUnitTax = new Decimal(getInclusiveTax(discountedPrice.toNumber(), rateDecimal.toNumber()));
            rawTotalTax = singleUnitTax.times(absQuantity);

            const roundedSingleUnitTax = new Decimal(setTaxRound(singleUnitTax.toNumber()));
            totalTax = roundedSingleUnitTax.times(absQuantity);
          }
        } else {
          if (isNoRounding) {
            totalTax = new Decimal(getExclusiveTax(totalPrice.toNumber(), rateDecimal.toNumber()));
            rawTotalTax = totalTax;
          } else {
            const singleUnitTax = new Decimal(getExclusiveTax(discountedPrice.toNumber(), rateDecimal.toNumber()));
            rawTotalTax = singleUnitTax.times(absQuantity);

            const roundedSingleUnitTax = new Decimal(setTaxRound(singleUnitTax.toNumber()));
            totalTax = roundedSingleUnitTax.times(absQuantity);
          }
        }

        totalTax = totalTax.times(quantitySign);
        rawTotalTax = rawTotalTax.times(quantitySign);

        const totalModTaxSum = modTaxSum.times(absQuantity).times(quantitySign);
        const modRawAdd = isNoRounding ? totalModTaxSum : new Decimal(0);

        itemRawTaxTotal = itemRawTaxTotal.plus(rawTotalTax).plus(modRawAdd);

        if (tax_rate.id) {
          let finalTotalTax;
          let perUnitTax;

          if (isNoRounding) {
            finalTotalTax = totalTax.plus(totalModTaxSum);
            perUnitTax = finalTotalTax.dividedBy(absQuantity);
          } else {
            const combinedTotalTax = totalTax.plus(totalModTaxSum);
            finalTotalTax = new Decimal(setTaxRound(combinedTotalTax.toNumber()));
            perUnitTax = finalTotalTax.dividedBy(absQuantity);
          }

          const calculated_tax_item = {
            local_id: `${self.terminal.id}_${Date.now()}`,
            tax_rate: tax_rate.id,
            tax_rate_value: tax_rate.rate,
            tax_value_item: perUnitTax.toNumber(),
            tax_value_modifiers: modTaxSum.times(quantitySign).toNumber(),
            name: tax_rate.name,
            tax_total: finalTotalTax.toNumber(),
            _refund: item.quantity < 0,
            _quantity: absQuantity.toNumber(),
            _raw_tax_value_item: rawTotalTax.dividedBy(absQuantity).toNumber(),
            _raw_tax_total: rawTotalTax.toNumber()
          };
          tax_items.push(calculated_tax_item);
        }
      }

      item._raw_tax = itemRawTaxTotal.toNumber();

      return tax_items;
    },
    calculateAllItemTaxes: function () {
      if (!this.products || !this.currentOrder || !this.currentOrder.items) return;

      const ROUNDING_TYPE_NEAREST = 'nearest';
      const ROUNDING_TYPE_ORDER_LEVEL_DISTRIBUTED = 'order_level_distributed';

      // Get the store's tax rounding setting
      const tempStore = this.$store.getters["config/store"];
      const taxRoundingMode = tempStore && 'tax_round' in tempStore ? tempStore.tax_round : ROUNDING_TYPE_NEAREST;

      // 1. Standard Calculation Loop (Recalculate everyone first)
      for (const index of this.currentOrder.items.keys()) {
        const item = this.currentOrder.items[index];

        // Reset if voided or non-taxable
        if (item.void_status || item.tax_status !== 0) {
          this.currentOrder.items[index].tax_items = [];
          this.currentOrder.items[index].tax = 0;
          continue;
        }

        // Calculate tax items using our new Decimal-based logic
        const tax_items = this.calculateItemTaxItems(item);
        this.currentOrder.items[index].tax_items = tax_items;

        // Calculate total tax sum using Decimal logic
        const taxSum = this.getItemAllTaxSum(this.currentOrder.items[index]);
        this.currentOrder.items[index].tax = taxSum;

        // Update price_before_tax for Inclusive Tax mode (Crucial for UI/Receipts)
        if (this.isTaxInclusive) {
          const price = new Decimal(item.price || 0);
          const tax = new Decimal(taxSum);
          this.currentOrder.items[index].price_before_tax = price.minus(tax).toNumber();
        }
      }

      // 2. Order Level Distributed Tax Logic (If enabled)
      if (taxRoundingMode === ROUNDING_TYPE_ORDER_LEVEL_DISTRIBUTED) {
        const positiveItems = [];
        const negativeItems = [];

        // Prepare items for distribution
        for (const item of this.currentOrder.items) {
          if (item.void_status || item.tax_status !== 0 || !item.tax_items) continue;

          const absQuantity = Math.abs(item.quantity || 0);
          const quantitySign = Math.sign(item.quantity) || 1;

          for (const taxItem of item.tax_items) {
            // Calculate discounted price
            const wholeDiscountVal = item.wholeDiscount < 0 ? (-1 * item.wholeDiscount) : item.wholeDiscount;
            const discountedPrice = (item.price_base || item.price || 0) - ((item.discount || 0) + (wholeDiscountVal || 0));

            // Calculate modifier totals with discounts
            let modifiersTotal = 0;
            if (item["modifiers"] && Array.isArray(item["modifiers"])) {
              const orderDiscountRatio = typeof item.orderDiscountRatio === 'number' ? item.orderDiscountRatio : 0;
              for (const mod of item["modifiers"].filter(m => m && m.void_status !== true)) {
                const modPrice = mod.price_original != null ? mod.price_original : (mod.price || 0);
                const discountedModPrice = modPrice * (1 - orderDiscountRatio);
                modifiersTotal += discountedModPrice;
              }
            }

            // Total price for distribution (Base + Mods) * Qty
            const totalPriceWithModifiers = (discountedPrice + modifiersTotal) * absQuantity;

            // Generate unique ID for matching
            const distributionId = `${item.local_id || item.id}_${taxItem.tax_rate}`;

            // Tag the tax item so we can find it later
            taxItem._distributionId = distributionId;

            const distributionItem = {
              itemId: distributionId,
              taxRateId: taxItem.tax_rate,
              price: totalPriceWithModifiers,
              quantity: 1, // Already multiplied by qty above
              tax_rate: taxItem.tax_rate_value / 100,
              original_tax: Math.abs(taxItem.tax_total),
              _quantitySign: quantitySign
            };

            if (quantitySign < 0) {
              negativeItems.push(distributionItem);
            } else {
              positiveItems.push(distributionItem);
            }
          }
        }

        // Helper to apply results back to order items
        const applyDistributionResults = (result, quantitySign) => {
          if (!result || !result.items) return;

          for (const distributedItem of result.items) {
            // Find the item that owns this tax distribution
            for (const orderItem of this.currentOrder.items) {
              if (!orderItem.tax_items) continue;

              const targetTaxItem = orderItem.tax_items.find(ti => ti._distributionId === distributedItem.itemId);

              if (targetTaxItem) {
                // Apply allocated tax with correct sign
                const allocatedTax = Math.abs(distributedItem.allocated_tax) * quantitySign;
                const absQuantity = Math.abs(orderItem.quantity);

                // Update the tax item
                targetTaxItem.tax_total = allocatedTax;
                // Avoid division by zero
                targetTaxItem.tax_value_item = absQuantity > 0 ? (allocatedTax / absQuantity) : 0;

                // Update the parent item total tax
                orderItem.tax = this.getItemAllTaxSum(orderItem);

                // Re-update price_before_tax for inclusive mode after distribution adjustment
                if (this.isTaxInclusive) {
                  const price = new Decimal(orderItem.price || 0);
                  const tax = new Decimal(orderItem.tax);
                  orderItem.price_before_tax = price.minus(tax).toNumber();
                }
                break;
              }
            }
          }
        };

        // Execute Distribution
        if (positiveItems.length > 0) {
          try {
            const positiveResult = calculateOrderLevelDistributedTax(positiveItems, this.isTaxInclusive);
            applyDistributionResults(positiveResult, 1);
          } catch (error) {
            console.error("Error calculating positive distributed tax", error);
          }
        }

        if (negativeItems.length > 0) {
          try {
            const negativeResult = calculateOrderLevelDistributedTax(negativeItems, this.isTaxInclusive);
            applyDistributionResults(negativeResult, -1);
          } catch (error) {
            console.error("Error calculating negative distributed tax", error);
          }
        }

        // Update _lastDistributionResult for calculateOrderTax usage
        let combinedTotalTax = 0;
        for (const item of this.currentOrder.items) {
          if (!item.void_status && item.tax_status === 0 && item.tax_items) {
            for (const taxItem of item.tax_items) {
              combinedTotalTax += taxItem.tax_total || 0;
            }
          }
        }
        this._lastDistributionResult = { total_tax: combinedTotalTax };
      }
    }
  }
};
